/* eslint-disable no-shadow */
import {
  COMMENT_ADD,
  COMMENT_DELETE,
  COMMENT_GET,
  COMMENT_GET_WITH_USER,
  COMMENT_UPDATE,
  GQL_FIND_CHILD_COMMENTS,
  GQL_LOAD_COMMENTS,
  GQL_LOAD_COMMENTS_WITH_USER,
} from "@/graphql/comment.graphql";
import {
  CommentScope,
  IAddCommentPayload,
  ICommentContent,
  ICommentState,
} from "@/interfaces/comment.interface";
import { Order } from "@/interfaces/common.interface";
import { showCollectorCommentsState } from "@/stores/collection";
import {
  batchCommentLoadingState,
  childCollectorCommentMergeField,
  childCollectorCommentState,
  childCommentMergeField,
  childCommentState,
  childUserCommentMergeField,
  childUserCommentState,
  collectorCommentMergeField,
  collectorCommentState,
  commentMergeField,
  commentState,
  userCommentMergeField,
  userCommentState,
} from "@/stores/comment";
import { currentUserState } from "@/stores/common";
import { metaState } from "@/stores/meta";
import { parseChildCommentMeta, parseManyComments } from "@/utils/comment.util";
import { useLazyQuery, useMutation, useQuery } from "@apollo/react-hooks";
import { message } from "antd";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useLazyMeta } from "./useMeta";
import useTranslation from "./useTranslation";

export const useUpdateComment = (
  fatherId: string,
  refetch: () => Promise<void>
) => {
  const { t } = useTranslation("common");

  const [_updateComment, { loading }] = useMutation<
    { updateComment: boolean },
    {
      comment: {
        id: string;
        content: string;
        response: string;
      };
    }
  >(COMMENT_UPDATE);

  const { getMeta } = useLazyMeta(fatherId);

  const updateComment = async ({
    fatherId: id,
    content,
    response,
  }: {
    fatherId: string;
    content: string;
    response: string;
  }) => {
    const {
      data: { updateComment },
    } = await _updateComment({
      variables: { comment: { id, content, response } },
    });
    if (updateComment) {
      await refetch();
      message.success(t("comment.update.message"));
    }
    getMeta();
  };

  return {
    updateComment,
    loading,
  };
};

export const useDeleteComment = (
  fatherId: string,
  refetch: () => Promise<void>
) => {
  const { t } = useTranslation("common");
  const [_deleteComment, { loading }] = useMutation<
    { deleteComment: boolean },
    { id: string }
  >(COMMENT_DELETE);
  const { getMeta } = useLazyMeta(fatherId);

  const deleteComment = async (id: string) => {
    const {
      data: { deleteComment },
    } = await _deleteComment({ variables: { id } });
    if (deleteComment) {
      await refetch();
    }
    getMeta();
    message.success(t("comment.delete.message"));
  };
  return {
    deleteComment,
    loading,
  };
};

export const useAddComment = (
  fatherId: string,
  refetch: () => Promise<void>
) => {
  const router = useRouter();
  const [_addComment, { loading }] = useMutation<
    { addComment: ICommentContent },
    { comment: IAddCommentPayload }
  >(COMMENT_ADD);
  const { getMeta } = useLazyMeta(fatherId);
  const addComment = async (props: {
    fatherId: string;
    content: string;
    response: string;
    parentId?: string;
    url?: string;
  }) => {
    await _addComment({
      variables: {
        comment: {
          ...props,
          url: props.url ?? router.asPath,
        },
      },
    });
    await refetch();
    getMeta();
  };
  return {
    addComment,
    loading,
  };
};

export const useSwitchSetCommentScope = (scope: CommentScope) => {
  switch (scope) {
    case "all":
      return useSetRecoilState(commentState);
    case "user":
      return useSetRecoilState(userCommentState);
    case "collector":
      return useSetRecoilState(collectorCommentState);
    default:
      return useSetRecoilState(commentState);
  }
};

export const useSwitchSetChildCommentScope = (scope: CommentScope) => {
  switch (scope) {
    case "all":
      return useSetRecoilState(childCommentState);
    case "user":
      return useSetRecoilState(childUserCommentState);
    case "collector":
      return useSetRecoilState(childCollectorCommentState);
    default:
      return useSetRecoilState(commentState);
  }
};

export const useSwitchGetChildCommentScope = (
  scope: CommentScope,
  fatherId: string
) => {
  switch (scope) {
    case "all":
      return useRecoilValue<ICommentState>(childCommentMergeField(fatherId));
    case "user":
      return useRecoilValue<ICommentState>(
        childUserCommentMergeField(fatherId)
      );
    case "collector":
      return useRecoilValue<ICommentState>(
        childCollectorCommentMergeField(fatherId)
      );
    default:
      return useRecoilValue<ICommentState>(childCommentMergeField(fatherId));
  }
};

export const useSwitchGetCommentScope = (
  scope: CommentScope,
  fatherId: string
) => {
  switch (scope) {
    case "all":
      return useRecoilValue<ICommentState>(commentMergeField(fatherId));
    case "user":
      return useRecoilValue<ICommentState>(userCommentMergeField(fatherId));
    case "collector":
      return useRecoilValue<ICommentState>(
        collectorCommentMergeField(fatherId)
      );
    default:
      return useRecoilValue<ICommentState>(commentMergeField(fatherId));
  }
};

export const useSwitchSetMergeCommentScope = (
  scope: CommentScope,
  fatherId: string
) => {
  switch (scope) {
    case "all":
      return useSetRecoilState<ICommentState>(commentMergeField(fatherId));
    case "user":
      return useSetRecoilState<ICommentState>(userCommentMergeField(fatherId));
    case "collector":
      return useSetRecoilState<ICommentState>(
        collectorCommentMergeField(fatherId)
      );
    default:
      return useSetRecoilState<ICommentState>(commentMergeField(fatherId));
  }
};

export const useSwitchSetMergeChildCommentScope = (
  scope: CommentScope,
  fatherId: string
) => {
  switch (scope) {
    case "all":
      return useSetRecoilState<ICommentState>(childCommentMergeField(fatherId));
    case "user":
      return useSetRecoilState<ICommentState>(
        childUserCommentMergeField(fatherId)
      );
    case "collector":
      return useSetRecoilState<ICommentState>(
        childCollectorCommentMergeField(fatherId)
      );
    default:
      return useSetRecoilState<ICommentState>(childCommentMergeField(fatherId));
  }
};

export const useComment = (fatherId: string, scope: CommentScope) => {
  const setComments = useSwitchSetCommentScope(scope);
  const setChildComments = useSwitchSetChildCommentScope(scope);
  const currentUser = useRecoilValue(currentUserState);
  const [{ page, size }, setPageSize] = useState<{
    page: number;
    size: number;
  }>({ page: 1, size: 5 });
  const defaultOrder = scope === "all" ? Order.ASC : Order.DESC;
  const SCHEMA = scope === "all" ? COMMENT_GET : COMMENT_GET_WITH_USER;

  const [order, setOrder] = useState<Order>(defaultOrder);
  const setMeta = useSetRecoilState(metaState);
  const { data, loading: loadingGet, refetch: _refetch } = useQuery<
    { findParentComments: ICommentState },
    { id: string; take?: number; skip?: number; userId?: string; order: Order }
  >(SCHEMA, {
    variables: {
      id: fatherId,
      skip: (page - 1) * size,
      take: size,
      userId: currentUser.id,
      order,
    },
    skip: !fatherId,
  });

  const refetch = async () => {
    const { data } = await _refetch({
      id: fatherId,
      skip: (page - 1) * size,
      take: size,
      userId: currentUser.id,
      order,
    });
    parseManyComments(
      [{ ...data?.findParentComments, fatherId }],
      setComments,
      setChildComments,
      setMeta
    );
  };

  useMemo(() => {
    if (data) {
      parseManyComments(
        [{ ...data?.findParentComments, fatherId }],
        setComments,
        setChildComments,
        setMeta
      );
    }
  }, [data]);

  const { addComment, loading: loadingAdd } = useAddComment(fatherId, refetch);
  const { updateComment, loading: loadingUpdate } = useUpdateComment(
    fatherId,
    refetch
  );
  const { deleteComment, loading: loadingDelete } = useDeleteComment(
    fatherId,
    refetch
  );

  return {
    setPageSize,
    setOrder,
    refetch,
    addComment,
    updateComment,
    deleteComment,
    page,
    size,
    loadingAdd,
    loadingGet,
    loadingUpdate,
    loadingDelete,
    loading: loadingAdd || loadingGet || loadingUpdate || loadingDelete,
  };
};

export const useLazyComment = (fatherId: string, scope: CommentScope) => {
  const setComments = useSwitchSetCommentScope(scope);
  const setChildComments = useSwitchSetChildCommentScope(scope);
  const currentUser = useRecoilValue(currentUserState);
  const SCHEMA = scope === "all" ? COMMENT_GET : COMMENT_GET_WITH_USER;
  const [{ page, size }, _setPageSize] = useState<{
    page: number;
    size: number;
  }>({ page: 1, size: 5 });
  const defaultOrder = scope === "all" ? Order.ASC : Order.DESC;
  const { userId: _userId } = useRecoilValue(showCollectorCommentsState);
  const userId = scope === "collector" ? _userId : currentUser.id;
  const [order, setOrder] = useState<Order>(defaultOrder);
  const [_getComments, { data, loading: loadingGet }] = useLazyQuery<
    { findParentComments: ICommentState },
    { id: string; take?: number; skip?: number; userId?: string; order: Order }
  >(SCHEMA, { ssr: false, fetchPolicy: "cache-and-network" });
  const setMeta = useSetRecoilState(metaState);
  const refetch = async () => {
    _getComments({
      variables: {
        id: fatherId,
        take: size,
        skip: (page - 1) * size,
        userId,
        order,
      },
    });
  };
  const setPageSize = ({ page, size }: { page: number; size: number }) => {
    _setPageSize({ page, size });
    _getComments({
      variables: {
        id: fatherId,
        take: size,
        skip: (page - 1) * size,
        userId,
        order,
      },
    });
  };
  useEffect(() => {
    if (data) {
      parseManyComments(
        [{ ...data?.findParentComments, fatherId }],
        setComments,
        setChildComments,
        setMeta
      );
    }
  }, [data]);

  const getComments = () =>
    _getComments({
      variables: {
        id: fatherId,
        take: size,
        skip: (page - 1) * size,
        userId,
        order,
      },
    });

  const { addComment, loading: loadingAdd } = useAddComment(fatherId, refetch);
  const { updateComment, loading: loadingUpdate } = useUpdateComment(
    fatherId,
    refetch
  );
  const { deleteComment, loading: loadingDelete } = useDeleteComment(
    fatherId,
    refetch
  );
  return {
    refetch,
    setOrder,
    setPageSize,
    addComment,
    updateComment,
    deleteComment,
    getComments,
    page,
    size,
    loadingAdd,
    loadingGet,
    loadingUpdate,
    loadingDelete,
    loading: loadingAdd || loadingGet || loadingUpdate || loadingDelete,
  };
};

export const useLazyChildComment = (parentId: string, scope: CommentScope) => {
  const setChildComments = useSwitchSetMergeChildCommentScope(scope, parentId);
  const currentUser = useRecoilValue(currentUserState);
  const setMeta = useSetRecoilState(metaState);
  const [{ page, size }, _setPageSize] = useState<{
    page: number;
    size: number;
  }>({ page: 1, size: 5 });
  const [_getComments, { data, loading: loadingGet }] = useLazyQuery<
    { findChildComments: ICommentState },
    { id: string; take?: number; skip?: number; userId?: string }
  >(GQL_FIND_CHILD_COMMENTS, { fetchPolicy: "cache-and-network", ssr: false });

  const refetch = async () => {
    _getComments({
      variables: {
        id: parentId,
        take: size,
        skip: (page - 1) * size,
        userId: currentUser.id,
      },
    });
  };

  const setPageSize = ({ page, size }: { page: number; size: number }) => {
    _setPageSize({ page, size });
    _getComments({
      variables: {
        id: parentId,
        take: size,
        skip: (page - 1) * size,
        userId: currentUser.id,
      },
    });
  };

  useEffect(() => {
    if (data) {
      parseChildCommentMeta(data?.findChildComments, setChildComments, setMeta);
    }
  }, [data]);

  const { addComment, loading: loadingAdd } = useAddComment(parentId, refetch);
  const { updateComment, loading: loadingUpdate } = useUpdateComment(
    parentId,
    refetch
  );
  const { deleteComment, loading: loadingDelete } = useDeleteComment(
    parentId,
    refetch
  );

  return {
    setPageSize,
    refetch,
    addComment,
    updateComment,
    deleteComment,
    page,
    size,
    loadingAdd,
    loadingGet,
    loadingUpdate,
    loadingDelete,
    loading: loadingAdd || loadingGet || loadingUpdate || loadingDelete,
  };
};

export const useComments = (
  fatherIds: string[],
  skip: boolean,
  scope: CommentScope
) => {
  const setComments = useSwitchSetCommentScope(scope);
  const setChildComments = useSwitchSetChildCommentScope(scope);
  const setMeta = useSetRecoilState(metaState);
  const currentUser = useRecoilValue(currentUserState);
  const SCHEMA =
    scope === "all" ? GQL_LOAD_COMMENTS : GQL_LOAD_COMMENTS_WITH_USER;
  const { userId: _userId } = useRecoilValue(showCollectorCommentsState);
  const userId = scope === "collector" ? _userId : currentUser.id;
  const defaultOrder = scope === "all" ? Order.ASC : Order.DESC;
  const { data, loading } = useQuery<
    { findManyParentComments: ICommentState[] },
    {
      fatherIds: string[];
      take?: number;
      skip?: number;
      userId?: string;
      order: Order;
    }
  >(SCHEMA, {
    variables: {
      fatherIds,
      userId,
      order: defaultOrder,
    },
    skip,
  });

  const [bLoading, setBLoading] = useRecoilState(batchCommentLoadingState);

  useMemo(() => {
    if (data) {
      parseManyComments(
        data?.findManyParentComments,
        setComments,
        setChildComments,
        setMeta
      );
    }
  }, [data]);

  useEffect(() => {
    setBLoading(loading);
  }, [loading]);

  return { loading };
};
